/**
 * Asks: what a model step asks the model for. Each ask brings the schema
 * fields it wants answered and turns its part of the answer into candidates.
 *
 * To ask for something new, add an ask here: its schema properties (or, for
 * something every item carries, `itemProperties`), the fields it provides,
 * and `parse`. The schema is put together from the asks in a step's `ask`
 * list, in production's field order (`FIELD_ORDER`), so the production asks
 * ask exactly what `callOpenAi` asks.
 */

import { lineFiling, RECEIPT_JSON_SCHEMA, receiptJsonSchema } from '../../extraction.js';
import type { Candidate, FieldKey } from '../types.js';

export type AskName = 'header' | 'items' | 'item-refs' | 'receipt-category' | 'item-categories';

export interface Ask {
  name: AskName;
  /** Top-level schema properties. */
  properties: Record<string, unknown>;
  /** Properties added to each item, whichever item ask is in the step. */
  itemProperties?: Record<string, unknown>;
  /** Whether it files the receipt, so it needs the category list to be sent. */
  files?: boolean;
  /** The fields it adds candidates for, for spec checking. */
  provides: FieldKey[];
  /**
   * Its part of the answer as candidates from `source`. A field answered
   * `null` is a candidate too: the model's word that it is not printed.
   */
  parse(raw: Record<string, unknown>, source: string, asks: readonly AskName[]): Partial<Record<FieldKey, Candidate[]>>;
}

const properties = RECEIPT_JSON_SCHEMA.properties;
/** Production's filing fields, taken from its own schema so the two cannot drift apart. */
const filing = receiptJsonSchema(true).properties as Record<string, { items?: { properties: Record<string, unknown> } }>;

/** Production's field order: `RECEIPT_JSON_SCHEMA`, then the filing fields `receiptJsonSchema` adds. */
export const FIELD_ORDER: readonly string[] = [...RECEIPT_JSON_SCHEMA.required, 'category', 'otherCategories'];

/** The header fields a model answers under their own names. */
const HEADER_FIELDS = [
  'purchasedAt', 'currency', 'total', 'subtotal', 'discountTotal', 'roundingAmount', 'depositTotal', 'vatLines',
  'paymentMethod', 'cardLast4', 'receiptNumber', 'terminalId', 'cashier', 'confidence',
] as const satisfies readonly FieldKey[];

type Candidates = Partial<Record<FieldKey, Candidate[]>>;

function add(out: Candidates, field: FieldKey, value: unknown, candidate: Omit<Candidate, 'value'>): void {
  if (value === undefined) return;
  out[field] = [...(out[field] ?? []), { value, ...candidate }];
}

/** The store, date, amounts, VAT table and payment, as printed. */
export const headerAsk: Ask = {
  name: 'header',
  properties: Object.fromEntries(Object.entries(properties).filter(([key]) => key !== 'items')),
  provides: ['merchant', 'orgNumber', ...HEADER_FIELDS],
  parse(raw, source) {
    const out: Candidates = {};
    const merchant = raw.merchant && typeof raw.merchant === 'object' && !Array.isArray(raw.merchant) ? (raw.merchant as Record<string, unknown>) : null;
    if (merchant) {
      const { orgNumber, ...rest } = merchant;
      add(out, 'merchant', rest, { source });
      add(out, 'orgNumber', orgNumber ?? null, { source });
    } else {
      add(out, 'merchant', null, { source });
    }
    for (const field of HEADER_FIELDS) add(out, field, raw[field], { source });
    return out;
  },
};

function itemsOf(raw: Record<string, unknown>): unknown[] | undefined {
  return Array.isArray(raw.items) ? raw.items : undefined;
}

/** Every printed line with its amounts, copied by the model (production's). */
export const itemsAsk: Ask = {
  name: 'items',
  properties: { items: properties.items },
  provides: ['items'],
  parse(raw, source) {
    const out: Candidates = {};
    add(out, 'items', itemsOf(raw), { source });
    return out;
  },
};

/**
 * The product lines by the number of the OCR line each amount is printed on,
 * with the name and amount as printed: a `ground-items` step finds each line
 * by its number and name and reads the amount off it, where OCR reads digits
 * better than a model copies them - the model's amount is only the fallback
 * when no line has one. The answer stays in the step's answer for that step;
 * it adds no candidates.
 */
export const itemRefsAsk: Ask = {
  name: 'item-refs',
  properties: {
    items: {
      type: 'array',
      description: 'Every product, deposit and discount line, in order, by the number of the OCR line its amount is printed on.',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['line', 'name', 'price'],
        properties: {
          line: { type: 'integer', description: 'The number of the OCR line the amount of this item is printed on.' },
          name: { type: 'string', description: 'The product name as printed on the receipt.' },
          price: { type: ['string', 'null'], description: 'The line total as printed, e.g. "12,50" or "25,00-".' },
        },
      },
    },
  },
  provides: [],
  parse: () => ({}),
};

/** The receipt's main category and the others it covers. */
export const receiptCategoryAsk: Ask = {
  name: 'receipt-category',
  properties: { category: filing.category, otherCategories: filing.otherCategories },
  files: true,
  provides: ['receiptCategory', 'otherCategories'],
  parse(raw, source) {
    const out: Candidates = {};
    add(out, 'receiptCategory', raw.category, { source });
    if (Array.isArray(raw.otherCategories)) add(out, 'otherCategories', raw.otherCategories, { source });
    return out;
  },
};

/** A category on every item - with how sure the model is and what else it could be - filing the item list of the same answer. */
const itemFiling = filing.items!.items!.properties;
export const itemCategoriesAsk: Ask = {
  name: 'item-categories',
  properties: {},
  itemProperties: {
    category: itemFiling.category,
    categoryConfidence: itemFiling.categoryConfidence,
    alternativeCategories: itemFiling.alternativeCategories,
  },
  files: true,
  provides: ['itemCategories'],
  parse(raw, source, asks) {
    const out: Candidates = {};
    const items = itemsOf(raw);
    // Categories for item references are carried over by `ground-items`, which knows where each item went.
    if (items && asks.includes('items')) {
      const categories = items.map((item) => (item && typeof item === 'object' ? lineFiling(item as Record<string, unknown>) : null));
      add(out, 'itemCategories', categories, { source, forItems: source });
    }
    return out;
  },
};

export const ASKS: Record<AskName, Ask> = {
  header: headerAsk,
  items: itemsAsk,
  'item-refs': itemRefsAsk,
  'receipt-category': receiptCategoryAsk,
  'item-categories': itemCategoriesAsk,
};

/**
 * The schema for `asks`, fields in production's order. Filing asks are left
 * out when the category list is not in the prompt.
 */
export function schemaFor(asks: readonly AskName[], withCategories: boolean): Record<string, unknown> {
  const chosen = asks.map((name) => ASKS[name]).filter((ask) => withCategories || !ask.files);
  const props: Record<string, unknown> = Object.assign({}, ...chosen.map((ask) => ask.properties));
  const itemExtra: Record<string, unknown> = Object.assign({}, ...chosen.map((ask) => ask.itemProperties ?? {}));
  const items = props.items as { items: { required: string[]; properties: Record<string, unknown> } } | undefined;
  if (items && Object.keys(itemExtra).length > 0) {
    props.items = {
      ...items,
      items: {
        ...items.items,
        required: [...items.items.required, ...Object.keys(itemExtra)],
        properties: { ...items.items.properties, ...itemExtra },
      },
    };
  }
  const order = [...FIELD_ORDER.filter((key) => key in props), ...Object.keys(props).filter((key) => !FIELD_ORDER.includes(key))];
  return {
    type: 'object',
    additionalProperties: false,
    required: order,
    properties: Object.fromEntries(order.map((key) => [key, props[key]])),
  };
}

/** Every candidate the asks find in an answer. */
export function parseAnswer(raw: Record<string, unknown>, source: string, asks: readonly AskName[]): Candidates {
  const out: Candidates = {};
  for (const name of asks) {
    for (const [field, list] of Object.entries(ASKS[name].parse(raw, source, asks))) {
      const key = field as FieldKey;
      out[key] = [...(out[key] ?? []), ...(list ?? [])];
    }
  }
  return out;
}
